import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { adminActionService } from '@/services/adminActionService';
import { useToast } from '@/hooks/use-toast';

interface ImpersonatedUser {
  id: string;
  email: string;
  display_name: string | null;
  username: string | null;
}

interface ImpersonationContextType {
  isImpersonating: boolean;
  impersonatedUser: ImpersonatedUser | null;
  startImpersonation: (user: ImpersonatedUser) => Promise<void>;
  endImpersonation: () => void;
  impersonationStartedAt: Date | null;
}

const ImpersonationContext = createContext<ImpersonationContextType | null>(null);

const IMPERSONATION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

export const ImpersonationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { toast } = useToast();
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonatedUser | null>(null);
  const [impersonationStartedAt, setImpersonationStartedAt] = useState<Date | null>(null);

  const isImpersonating = impersonatedUser !== null;

  // Auto-end impersonation after timeout
  useEffect(() => {
    if (!impersonationStartedAt) return;

    const timeoutId = setTimeout(() => {
      toast({
        title: 'Impersonation Ended',
        description: 'Impersonation session timed out after 30 minutes',
      });
      endImpersonation();
    }, IMPERSONATION_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [impersonationStartedAt]);

  const startImpersonation = useCallback(async (user: ImpersonatedUser) => {
    try {
      // Log impersonation start
      await adminActionService.logAction(
        'impersonation_start',
        user.id,
        `Started impersonating user: ${user.display_name || user.email}`,
        { impersonated_user: user }
      );

      setImpersonatedUser(user);
      setImpersonationStartedAt(new Date());

      toast({
        title: 'Impersonation Started',
        description: `Now viewing as ${user.display_name || user.email}. This will end automatically after 30 minutes.`,
      });
    } catch (err) {
      // Error starting impersonation - show toast to user
      toast({
        title: 'Error',
        description: 'Failed to start impersonation',
        variant: 'destructive',
      });
      throw err;
    }
  }, [toast]);

  const endImpersonation = useCallback(async () => {
    if (impersonatedUser) {
      try {
        // Log impersonation end
        await adminActionService.logAction(
          'impersonation_end',
          impersonatedUser.id,
          `Ended impersonation of user: ${impersonatedUser.display_name || impersonatedUser.email}`,
          { 
            duration_minutes: impersonationStartedAt 
              ? Math.round((Date.now() - impersonationStartedAt.getTime()) / 60000)
              : 0
          }
        );
      } catch {
        // Error logging impersonation end - non-critical
      }
    }

    setImpersonatedUser(null);
    setImpersonationStartedAt(null);
  }, [impersonatedUser, impersonationStartedAt]);

  return (
    <ImpersonationContext.Provider value={{
      isImpersonating,
      impersonatedUser,
      startImpersonation,
      endImpersonation,
      impersonationStartedAt
    }}>
      {children}
    </ImpersonationContext.Provider>
  );
};

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error('useImpersonation must be used within ImpersonationProvider');
  }
  return context;
};
